import { FC, useMemo } from 'react';
import { sanitizeHtml } from '@new/common/lib/sanitize';
import { type Project } from '@teachur/http-client';
import { ExampleFileLink } from '@new/common/ui/ExampleFileLink/ExampleFileLink';
import { FileSection } from '@new/features/lessons/components/FileSection/FileSection';
import { mapFileToItem } from '@new/features/lessons/lib/fileDataHelper';

interface ProjectInfoProps {
  project: Project;
  hasSubmissions: boolean;
}

export const ProjectInfo: FC<ProjectInfoProps> = ({ project, hasSubmissions }) => {
  const projectFiles = useMemo(
    () => project.files?.map((file) => mapFileToItem(file)).filter((file) => !!file) || [],
    [project.files]
  );

  const handleDownloadFile = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      {project.description && (
        <div className="flex flex-col gap-2">
          <h5 className="[font-size:var(--fs-l3)]! lg:[font-size:var(--fs-l2)]! font-bold text-(--neutral-black-NB1000)! m-0!">
            Project description
          </h5>
          <div className="flex flex-col gap-3">
            {!hasSubmissions && (
              <div className="flex w-full rounded-xl px-5 py-4 bg-(--warning-W100)">
                <p className="m-0! [font-size:var(--fs-l5)]! lg:[font-size:var(--fs-p3)]! font-medium text-(--warning-W700)">
                  Carefully review the project description and goals. The project will be reviewed
                  in light of these.
                </p>
              </div>
            )}
            <div
              className="flex max-w-full flex-col gap-3 overflow-hidden whitespace-normal [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! leading-[150%] text-(--neutral-black-NB900)! **:max-w-full **:whitespace-normal **:wrap-break-word [&_ol]:m-0! [&_p]:m-0! [&_ul]:m-0!"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(project.description) }}
            />
          </div>
        </div>
      )}

      {!!project.goals?.length && (
        <div className="flex flex-col gap-2">
          <h5 className="[font-size:var(--fs-l3)]! lg:[font-size:var(--fs-l2)]! font-bold text-(--neutral-black-NB1000)! m-0!">
            Project goals:
          </h5>

          <ul className="m-0! flex flex-col gap-2 p-0!">
            {project.goals.map((goal) => (
              <li
                key={goal.id}
                className="flex items-center gap-3 [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! text-(--neutral-black-NB900)! leading-[150%]"
              >
                <span className="h-3 lg:h-3.5 w-3 lg:w-3.5 shrink-0 bg-(--secondary-B200)" />
                {goal.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      <FileSection
        title="Project files"
        files={projectFiles}
        onDownload={handleDownloadFile}
        showStatus={false}
      />

      {project.exampleFileUrl && <ExampleFileLink url={project.exampleFileUrl} />}
    </>
  );
};
